import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  IconButton,
  Badge,
  Popover,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Button,
  Divider,
  CircularProgress
} from '@mui/material';
import {
  Bell,
  MessageSquare,
  FileText,
  RefreshCw,
  ToggleLeft,
  Check,
  CheckCheck
} from 'lucide-react';
import { useSocket } from '../context/SocketContext';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const EVENT_ICONS = {
  MESSAGES: MessageSquare,
  DOCUMENTS: FileText,
  STATUS: RefreshCw,
  OPTIONS: ToggleLeft
};

const EVENT_COLORS = {
  MESSAGES: '#00d4aa',
  DOCUMENTS: '#3b82f6',
  STATUS: '#f59e0b',
  OPTIONS: '#8b5cf6'
};

const formatTimeAgo = (date) => {
  const now = new Date();
  const diff = now - new Date(date);
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return 'À l\'instant';
  if (minutes < 60) return `Il y a ${minutes} min`;
  if (hours < 24) return `Il y a ${hours}h`;
  if (days < 7) return `Il y a ${days}j`;
  return new Date(date).toLocaleDateString('fr-CA');
};

export default function NotificationBell() {
  const { socket } = useSocket();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const isAdmin = user?.role === 'ADMIN' || user?.role === 'SUPERADMIN';
  const isInternalRole = isAdmin || user?.role === 'DEV' || user?.role === 'TECHNICIEN';

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    try {
      const token = localStorage.getItem('portalToken');
      const response = await fetch('/api/notifications?limit=20', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setNotifications(data);
      }
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    }
  }, []);

  // Fetch unread count
  const fetchUnreadCount = useCallback(async () => {
    try {
      const token = localStorage.getItem('portalToken');
      const response = await fetch('/api/notifications/unread', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUnreadCount(data.count);
      }
    } catch (err) {
      console.error('Failed to fetch unread count:', err);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchUnreadCount();
  }, [fetchUnreadCount]);

  // Socket listeners
  useEffect(() => {
    if (!socket) return;

    const handleNewNotification = (notification) => {
      setUnreadCount(prev => prev + 1);
      // Optionally add to list if popover is open
      if (anchorEl) {
        setNotifications(prev => [
          {
            id: Date.now().toString(),
            ...notification,
            isRead: false,
            createdAt: new Date().toISOString()
          },
          ...prev
        ].slice(0, 20));
      }
    };

    const handleCountUpdate = ({ count }) => {
      setUnreadCount(count);
    };

    socket.on('notification:new', handleNewNotification);
    socket.on('notification:countUpdate', handleCountUpdate);

    return () => {
      socket.off('notification:new', handleNewNotification);
      socket.off('notification:countUpdate', handleCountUpdate);
    };
  }, [socket, anchorEl]);

  // Handle popover open
  const handleClick = async (event) => {
    setAnchorEl(event.currentTarget);
    setLoading(true);
    await fetchNotifications();
    setLoading(false);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Mark all as read
  const handleMarkAllAsRead = async () => {
    try {
      const token = localStorage.getItem('portalToken');
      await fetch('/api/notifications/read', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({})
      });
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  };

  // Navigate to submission
  const handleNotificationClick = async (notif) => {
    // Mark as read
    if (!notif.isRead) {
      try {
        const token = localStorage.getItem('portalToken');
        await fetch(`/api/notifications/${notif.id}/read`, {
          method: 'PATCH',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setUnreadCount(prev => Math.max(0, prev - 1));
        setNotifications(prev => prev.map(n => 
          n.id === notif.id ? { ...n, isRead: true } : n
        ));
      } catch (err) {
        console.error('Failed to mark as read:', err);
      }
    }

    // Navigate
    handleClose();
    const basePath = isInternalRole ? '/admin' : '/portal';
    navigate(`${basePath}/submissions/${notif.submissionId}`);
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <IconButton
        onClick={handleClick}
        sx={{
          color: 'text.secondary',
          '&:hover': { color: 'primary.main' }
        }}
      >
        <Badge 
          badgeContent={unreadCount} 
          color="error"
          max={99}
          sx={{
            '& .MuiBadge-badge': {
              fontSize: '0.65rem',
              height: 18,
              minWidth: 18
            }
          }}
        >
          <Bell size={20} />
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            width: 360,
            maxHeight: 480,
            bgcolor: 'background.paper',
            backgroundImage: 'none',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 2
          }
        }}
      >
        {/* Header */}
        <Box sx={{ 
          p: 2, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          borderBottom: '1px solid rgba(255,255,255,0.1)'
        }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Notifications
          </Typography>
          {unreadCount > 0 && (
            <Button 
              size="small" 
              onClick={handleMarkAllAsRead}
              startIcon={<CheckCheck size={14} />}
              sx={{ fontSize: '0.75rem' }}
            >
              Tout marquer lu
            </Button>
          )}
        </Box>

        {/* Notifications list */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Bell size={32} color="#666" />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Aucune notification
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0, maxHeight: 360, overflowY: 'auto' }}>
            {notifications.map((notif, index) => {
              const Icon = EVENT_ICONS[notif.eventType] || Bell;
              const color = EVENT_COLORS[notif.eventType] || '#888';

              return (
                <React.Fragment key={notif.id}>
                  <ListItem
                    button
                    onClick={() => handleNotificationClick(notif)}
                    sx={{
                      bgcolor: notif.isRead ? 'transparent' : 'rgba(0,212,170,0.05)',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' },
                      py: 1.5
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: '6px',
                          bgcolor: `${color}20`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <Icon size={14} color={color} />
                      </Box>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: notif.isRead ? 400 : 600,
                              flex: 1,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {notif.projectTitle}
                          </Typography>
                          {!notif.isRead && (
                            <Box sx={{ 
                              width: 8, 
                              height: 8, 
                              borderRadius: '50%', 
                              bgcolor: '#00d4aa',
                              flexShrink: 0
                            }} />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              display: 'block',
                              color: 'text.secondary',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {notif.eventData?.authorName && `${notif.eventData.authorName} · `}
                            {notif.eventData?.content?.substring(0, 50) || 
                             (notif.eventType === 'MESSAGES' ? 'Nouveau message' :
                              notif.eventType === 'DOCUMENTS' ? 'Nouveau document' :
                              notif.eventType === 'STATUS' ? 'Statut mis à jour' :
                              'Nouvelle activité')}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.65rem' }}>
                            {formatTimeAgo(notif.createdAt)}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < notifications.length - 1 && (
                    <Divider sx={{ borderColor: 'rgba(255,255,255,0.05)' }} />
                  )}
                </React.Fragment>
              );
            })}
          </List>
        )}
      </Popover>
    </>
  );
}
